import { execSync } from 'node:child_process';
import { existsSync, readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { isAuthenticated, getDefaultConfigDir } from './auth.js';
import { apiGet } from './api-client.js';
import { GATEWAY_PORT, isGatewayListening } from './gateway-lifecycle.js';
import {
  detectInstalledTools,
  mergeClaudeCodeHooks,
  hasRulemetricClaudeHooks,
  removeRulemetricMachineState,
  RULEMETRIC_NO_PROXY,
} from './hooks-config.js';

/**
 * Data model for the four core onboarding steps, shared (by step-ID list +
 * install-monotonicity) with the web reducer `deriveSetupProgress()`.
 *
 * The two surfaces are separate implementations by necessity, but they no
 * longer read *different sources* for the same fact:
 *   - Server-observable signals (cli-worker heartbeat / captured session) are
 *     unified through the shared `GET /api/setup/status` endpoint — the CLI's
 *     `workerActive()` and the web's `use-setup-progress.ts` both bottom out on
 *     that one server-side authority, so they cannot drift on it.
 *   - Local signals (auth = `~/.config/rulemetric` creds via `isAuthenticated()`,
 *     hooks = cwd `.claude/settings.local.json`, falling back to the shared
 *     `settings.json` for legacy installs) stay machine-local: the browser
 *     cannot read the user's filesystem, so these legitimately differ from the
 *     web's account-scoped proxies for the same step (see the design doc's
 *     "Shared step model, two implementations").
 * Each step exposes:
 *   - detect(): is it done? (reads a local/remote signal, never mutates)
 *   - plan():   what would apply() write? (read-only, returns diffs/artifacts)
 *   - apply():  perform the step (thin — shells out to existing commands)
 */

export const CORE_STEP_IDS = ['install', 'auth', 'hooks', 'worker'] as const;
// Optional steps live in the walk (offered, recommended) but do NOT gate
// `coreComplete` — declining them still leaves the user fully onboarded. The
// proxy/CA capture now ships as the DEFAULT of the core `hooks` step (see that
// step's plan(), which fully discloses the invasive MITM/keychain side effects
// up front); it is not a separate opt-in step. The mechanism is retained for
// any future optional step.
export const OPTIONAL_STEP_IDS = [] as const;
export const ALL_STEP_IDS = [...CORE_STEP_IDS, ...OPTIONAL_STEP_IDS] as const;
export type StepId = (typeof ALL_STEP_IDS)[number];

export type ArtifactKind = 'file' | 'command' | 'service';

export interface Artifact {
  path: string;
  kind: ArtifactKind;
  before: string | null;
  after: string | null;
  /** True when `after` contains a masked secret (never the raw value). */
  masked?: boolean;
}

export interface StepPlan {
  id: StepId;
  title: string;
  command?: string;
  artifacts: Artifact[];
}

export interface SetupContext {
  projectPath: string;
  configDir: string;
}

export interface SetupStep {
  id: StepId;
  title: string;
  command?: string;
  /** Optional steps are offered in the walk but never gate `coreComplete`. */
  optional?: boolean;
  detect(ctx: SetupContext): Promise<boolean>;
  plan(ctx: SetupContext): Promise<StepPlan>;
  apply(ctx: SetupContext): Promise<void>;
}

export interface StepStatus {
  id: StepId;
  done: boolean;
}

export interface SetupStatus {
  steps: StepStatus[];
  coreComplete: boolean;
  currentStepId: StepId | null;
}

/** Build the default context from the current process (cwd + config dir). */
export function defaultContext(): SetupContext {
  return {
    projectPath: process.cwd(),
    configDir: getDefaultConfigDir(),
  };
}

const CERT_PATH = join(homedir(), '.mitmproxy', 'mitmproxy-ca-cert.pem');

/**
 * Mask a secret: keep the first 4 and last 4 characters, replace the middle
 * with asterisks. Strings too short to reveal safely are fully masked.
 */
export function maskToken(s: string): string {
  if (!s) return '';
  if (s.length <= 8) return '*'.repeat(s.length);
  const head = s.slice(0, 4);
  const tail = s.slice(-4);
  return `${head}${'*'.repeat(Math.max(4, s.length - 8))}${tail}`;
}

/**
 * Simple line-oriented +/- diff for terminal display. Not a real LCS — good
 * enough to show a human/agent exactly which lines an artifact would change.
 */
export function renderDiff(before: string | null, after: string | null): string {
  const beforeLines = before === null ? [] : before.replace(/\n$/, '').split('\n');
  const afterLines = after === null ? [] : after.replace(/\n$/, '').split('\n');
  const out: string[] = [];
  const max = Math.max(beforeLines.length, afterLines.length);
  for (let i = 0; i < max; i++) {
    const b = beforeLines[i];
    const a = afterLines[i];
    if (b === a) {
      out.push(` ${b}`);
    } else {
      if (b !== undefined) out.push(`-${b}`);
      if (a !== undefined) out.push(`+${a}`);
    }
  }
  return out.join('\n');
}

/** Read a single KEY=value from the env file, or null if absent. */
function readEnvValue(configDir: string, key: string): string | null {
  const envPath = join(configDir, 'env');
  if (!existsSync(envPath)) return null;
  try {
    const content = readFileSync(envPath, 'utf-8');
    const match = content.match(new RegExp(`^${key}=(.+)$`, 'm'));
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Is a CLI worker heartbeat active for the caller? (best-effort, network)
 *
 * Reads the shared `GET /api/setup/status` endpoint's `workerActive` field
 * rather than re-deriving it from `/api/workers/status`. That endpoint computes
 * the cli-worker heartbeat signal server-side using the SAME window +
 * staleness thresholds + `workerType === 'cli-worker'` filter the web strip
 * uses, so the CLI and web can't drift on this fact by construction (they used
 * to each filter/threshold client-side, which is exactly how they could
 * disagree). Fast-fails after 2.5s so `setup --json` / `setup plan` degrade to
 * "not running" instead of blocking on a stalled connection — detection is a
 * UX signal, not a correctness gate; offline → false.
 */
async function workerActive(): Promise<boolean> {
  if (!isAuthenticated()) return false;
  try {
    const status = await Promise.race([
      apiGet<{
        cliEverConnected?: boolean;
        anyCapturedSession?: boolean;
        workerActive?: boolean;
      }>('/api/setup/status'),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('setup/status timeout')), 2500),
      ),
    ]);
    return status?.workerActive === true;
  } catch {
    return false;
  }
}

/** Read a project `.claude/<fileName>` settings file for a read-only preview (no throw). */
function readProjectSettings(
  projectPath: string,
  fileName: string,
): { settings: Record<string, unknown>; before: string | null } {
  const settingsPath = join(projectPath, '.claude', fileName);
  let settings: Record<string, unknown> = {};
  let before: string | null = null;
  if (existsSync(settingsPath)) {
    try {
      before = readFileSync(settingsPath, 'utf-8').replace(/\n$/, '');
      settings = JSON.parse(before) as Record<string, unknown>;
    } catch {
      settings = {};
    }
  }
  return { settings, before };
}

/**
 * Compute the in-memory `.claude/settings.local.json` after the RuleMetric
 * session HOOKS are merged, WITHOUT touching disk. This is the §1 (hooks)
 * portion of the diff; the proxy env block that `hooks install` §2 also writes
 * is disclosed separately via projectProxyEnvSettings so the two changes read
 * distinctly. Machine-local state targets settings.local.json — the shared
 * settings.json is typically checked into git (see claudeSettingsInstallPath).
 */
function projectHooksSettings(projectPath: string): { before: string | null; after: string } {
  const { settings, before } = readProjectSettings(projectPath, 'settings.local.json');
  mergeClaudeCodeHooks(settings);
  return { before, after: JSON.stringify(settings, null, 2) };
}

/**
 * The proxy-env addition to `.claude/settings.local.json` that `hooks install`
 * §2 makes on the default path — for the disclosure diff only. Read-only.
 */
function projectProxyEnvSettings(projectPath: string): { before: string | null; after: string } {
  const { settings, before } = readProjectSettings(projectPath, 'settings.local.json');
  const env = { ...((settings.env as Record<string, string>) ?? {}) };
  env.HTTPS_PROXY = `http://localhost:${GATEWAY_PORT}`;
  env.NO_PROXY = RULEMETRIC_NO_PROXY;
  env.NODE_EXTRA_CA_CERTS = CERT_PATH;
  return { before, after: JSON.stringify({ ...settings, env }, null, 2) };
}

/**
 * The migration edit `hooks install` makes to the SHARED `.claude/settings.json`
 * when it still carries RuleMetric machine-local state (legacy installs wrote
 * hooks + proxy env there; that state moves to settings.local.json). Returns
 * null when there is nothing of ours to remove — including when the shared
 * file is absent or unparseable (install skips it then too). Read-only.
 */
function projectSharedSettingsMigration(projectPath: string): { before: string | null; after: string } | null {
  const { settings, before } = readProjectSettings(projectPath, 'settings.json');
  if (before === null) return null;
  const { removed } = removeRulemetricMachineState(settings, GATEWAY_PORT);
  if (!removed) return null;
  return { before, after: JSON.stringify(settings, null, 2) };
}

const WORKER_PLIST_PATH = join(homedir(), 'Library', 'LaunchAgents', 'com.rulemetric.worker.plist');
const WORKER_SYSTEMD_PATH = join(homedir(), '.config', 'systemd', 'user', 'rulemetric-worker.service');

/** Run a command with inherited stdio (interactive-friendly). Thin apply(). */
function runCommand(cmd: string): void {
  execSync(cmd, { stdio: 'inherit' });
}

export const STEPS: SetupStep[] = [
  {
    id: 'install',
    title: 'Install the CLI',
    command: 'npm install -g @rulemetric/cli',
    // Monotonic: the CLI must exist to produce any downstream signal, so it
    // greens off auth or worker presence (nothing local phones home before).
    async detect(): Promise<boolean> {
      return isAuthenticated() || (await workerActive());
    },
    async plan(): Promise<StepPlan> {
      return {
        id: 'install',
        title: 'Install the CLI',
        command: 'npm install -g @rulemetric/cli',
        artifacts: [
          {
            path: 'npm install -g @rulemetric/cli',
            kind: 'command',
            before: null,
            after: 'rulemetric --version   # verifies the binary is on PATH',
          },
        ],
      };
    },
    async apply(): Promise<void> {
      runCommand('npm install -g @rulemetric/cli');
    },
  },
  {
    id: 'auth',
    title: 'Authenticate',
    command: 'rulemetric auth login',
    async detect(): Promise<boolean> {
      return isAuthenticated();
    },
    async plan(ctx): Promise<StepPlan> {
      const envPath = join(ctx.configDir, 'env');
      // Guard the read like the sibling helpers (readEnvValue /
      // projectHooksSettings) do: a TOCTOU race or permission error between
      // existsSync and readFileSync must NOT throw out of plan(). Fall back to
      // a secret-free marker rather than crashing `setup plan`/`--show`.
      let before: string | null = null;
      if (existsSync(envPath)) {
        try {
          before = maskEnvFile(readFileSync(envPath, 'utf-8'));
        } catch {
          before = '(unreadable)';
        }
      }
      const rawToken = readEnvValue(ctx.configDir, 'RULEMETRIC_ACCESS_TOKEN');
      const tokenDisplay = rawToken ? maskToken(rawToken) : maskToken('<token-from-login>');
      // Reconstruct EXACTLY what `auth login`'s saveEnvFile writes so the diff
      // doesn't show phantom changes: line order [TOKEN, KEY?, URL?], the key
      // preserved (masked) when present, and the URL sourced from
      // process.env.RULEMETRIC_API_URL — the same value login passes — and OMITTED
      // when unset (login writes no URL line then). All masking preserved; the raw
      // token/key are never emitted.
      const afterLines = [`RULEMETRIC_ACCESS_TOKEN=${tokenDisplay}`];
      const rawApiKey = readEnvValue(ctx.configDir, 'RULEMETRIC_API_KEY');
      if (rawApiKey) {
        afterLines.push(`RULEMETRIC_API_KEY=${maskToken(rawApiKey)}`);
      }
      const loginUrl = process.env.RULEMETRIC_API_URL;
      if (loginUrl) {
        afterLines.push(`RULEMETRIC_API_URL=${loginUrl}`);
      }
      const after = afterLines.join('\n');
      return {
        id: 'auth',
        title: 'Authenticate',
        command: 'rulemetric auth login',
        artifacts: [
          {
            path: envPath,
            kind: 'file',
            before,
            after,
            masked: true,
          },
          // `auth login` (saveToken) also writes auth.json — disclose it so the
          // plan matches every file apply() touches.
          {
            path: join(ctx.configDir, 'auth.json'),
            kind: 'file',
            before: existsSync(join(ctx.configDir, 'auth.json')) ? '(existing session tokens)' : null,
            after: '{ accessToken, refreshToken, expiresAt, email }  — OAuth session tokens (secret)',
            masked: true,
          },
        ],
      };
    },
    async apply(): Promise<void> {
      runCommand('rulemetric auth login');
    },
  },
  {
    id: 'hooks',
    title: 'Install hooks + capture proxy',
    // Default onboarding installs the FULL capture path: session hooks PLUS the
    // MITM proxy (rendered-system-prompt / instruction linking + cross-tool
    // capture). Every invasive side effect is disclosed up front in plan()
    // below — nothing is silent. `hooks install` fails SAFE: if the CA sudo
    // trust is declined it degrades to hooks-only (no machine-wide proxy pin),
    // and HTTPS_PROXY is only pinned once the gateway actually answers on
    // :8787 — so the default never bricks Claude Code. detect() greens on the
    // capture hooks being present, so `coreComplete` is reachable even when the
    // user declines the proxy.
    command: 'rulemetric hooks install',
    async detect(ctx): Promise<boolean> {
      // Current installs write machine-local hooks to settings.local.json;
      // legacy installs wrote the shared settings.json. Claude Code merges and
      // runs hooks from both, so presence in EITHER file means capture is wired
      // up. Ready when capture hooks are actually present — NOT when a proxy
      // env var was written (that greened even against a dead proxy port). The
      // proxy is a best-effort upgrade layered on top; hooks presence is the
      // honest "capture is working" signal.
      for (const fileName of ['settings.local.json', 'settings.json']) {
        const settingsPath = join(ctx.projectPath, '.claude', fileName);
        if (!existsSync(settingsPath)) continue;
        try {
          const settings = JSON.parse(readFileSync(settingsPath, 'utf-8')) as Record<string, unknown>;
          if (hasRulemetricClaudeHooks(settings)) return true;
        } catch {
          /* unparseable — treat as absent */
        }
      }
      return false;
    },
    async plan(ctx): Promise<StepPlan> {
      const settingsPath = join(ctx.projectPath, '.claude', 'settings.local.json');
      const { before, after } = projectHooksSettings(ctx.projectPath);
      const artifacts: Artifact[] = [
        { path: settingsPath, kind: 'file', before, after },
        // Disclose what the hooks DO, not just that they're registered — these
        // are the privacy-relevant behaviors a user is consenting to.
        {
          path: 'what these hooks capture',
          kind: 'service',
          before: null,
          after:
            'session-end reimports the FULL transcript to the API; post-tool-use records each ' +
            'tool call and snapshots the working tree to a LOCAL shadow git ref (paper-trail, ' +
            'never pushed); user-prompt posts prompt text. All scoped to sessions in this project.',
        },
        // FULL DISCLOSURE of the proxy layer — every invasive side effect of
        // `hooks install` §2 is enumerated here so the default path hides
        // nothing. Each degrades gracefully (see the step comment): a declined
        // CA-trust sudo skips the machine-wide layer and stays hooks-only.
        {
          // The proxy env block `hooks install` §2 adds to the SAME
          // .claude/settings.local.json (HTTPS_PROXY / NO_PROXY / NODE_EXTRA_CA_CERTS).
          path: `${settingsPath} (proxy env block)`,
          kind: 'file',
          ...projectProxyEnvSettings(ctx.projectPath),
        },
        {
          path: 'system keychain — mitmproxy root CA',
          kind: 'service',
          before: existsSync(CERT_PATH) ? '(CA generated; trust state unchanged)' : '(no CA yet)',
          after:
            'Installs + trusts the mitmproxy CA (requires sudo). This lets the ' +
            'proxy DECRYPT Claude Code HTTPS request/response bodies — the ' +
            'capability that powers rendered-system-prompt / instruction linking. ' +
            'Decline the sudo prompt and setup stays hooks-only (still fully onboarded).',
        },
        {
          path: 'launchctl setenv HTTPS_PROXY (machine-wide, all GUI apps)',
          kind: 'command',
          before: null,
          after: `http://localhost:${GATEWAY_PORT}  — routes every GUI app's HTTPS through the local gateway (only when the CA is trusted AND :${GATEWAY_PORT} is live)`,
        },
        {
          path: 'VS Code + Cursor user settings (http.proxy)',
          kind: 'file',
          before: null,
          after: `http.proxy → http://localhost:${GATEWAY_PORT} (+ http.proxyStrictSSL=false)`,
        },
        {
          path: `gateway process on :${GATEWAY_PORT}`,
          kind: 'service',
          before: null,
          after: 'started (routes Claude Code → mitmproxy → Anthropic; falls back to direct if mitmproxy is down)',
        },
      ];
      // Migration disclosure: when a legacy/poisoned shared settings.json still
      // carries RuleMetric machine-local state, install strips it (the state
      // moves to settings.local.json above). That is a write to a typically
      // checked-in file, so the plan must name it.
      const sharedMigration = projectSharedSettingsMigration(ctx.projectPath);
      if (sharedMigration) {
        artifacts.push({
          path: `${join(ctx.projectPath, '.claude', 'settings.json')} (removes RuleMetric machine-local state left by an older install — see the local-file artifacts above for where it moves)`,
          kind: 'file',
          ...sharedMigration,
        });
      }
      // Workspace-level editor configs, only if those dirs exist here.
      for (const tool of detectInstalledTools(ctx.projectPath).filter((t) => t === 'cursor' || t === 'copilot_agent')) {
        artifacts.push({
          path: `${ctx.projectPath} (${tool} workspace proxy config)`,
          kind: 'file',
          before: null,
          after: `proxy → http://localhost:${GATEWAY_PORT}`,
        });
      }
      // Cross-tool USER-level hooks — disclosed here, and (post-F2) written ONLY
      // when the tool is already present so we never manufacture config for a
      // tool the user doesn't have.
      for (const [tool, home, file] of [
        ['Cursor', join(homedir(), '.cursor'), join(homedir(), '.cursor', 'hooks.json')],
        ['Antigravity/Gemini', join(homedir(), '.gemini'), join(homedir(), '.gemini', 'config', 'hooks.json')],
      ] as const) {
        if (existsSync(home)) {
          artifacts.push({
            path: `${file} (${tool} user hooks)`,
            kind: 'file',
            before: existsSync(file) ? '(existing config)' : null,
            after: 'adds RuleMetric session-capture hooks for this tool',
          });
        }
      }
      return {
        id: 'hooks',
        title: 'Install hooks + capture proxy',
        command: 'rulemetric hooks install',
        artifacts,
      };
    },
    async apply(): Promise<void> {
      runCommand('rulemetric hooks install');
    },
  },
  {
    id: 'worker',
    title: 'Run the worker',
    command: 'rulemetric service install --worker-only',
    async detect(): Promise<boolean> {
      return workerActive();
    },
    async plan(): Promise<StepPlan> {
      const isMac = process.platform === 'darwin';
      const servicePath = isMac ? WORKER_PLIST_PATH : WORKER_SYSTEMD_PATH;
      // SECURITY: never read the installed plist/systemd unit into `before` —
      // those files embed RULEMETRIC_ACCESS_TOKEN / RULEMETRIC_API_KEY verbatim
      // (service/install.ts writes them from ALLOWED_ENV_VARS), so echoing the
      // file contents would leak the raw secret through `plan`, `--show`, and
      // `--json`. Use a synthetic, secret-free marker for the current state.
      const before = existsSync(servicePath)
        ? `(service already installed at ${servicePath})`
        : null;
      // Concise representation (path + ExecStart node/cli invocation) rather
      // than reconstructing the full plist — keep read-only + legible.
      const after = isMac
        ? [
            `Label: com.rulemetric.worker`,
            `ProgramArguments: <node> <cli>/bin/run.js evals agent`,
            `RunAtLoad: true, KeepAlive: true`,
          ].join('\n')
        : [
            `[Service]`,
            `ExecStart=<node> <cli>/bin/run.js evals agent`,
            `Restart=on-failure`,
          ].join('\n');
      return {
        id: 'worker',
        title: 'Run the worker',
        command: 'rulemetric service install --worker-only',
        artifacts: [
          { path: servicePath, kind: 'service', before, after },
        ],
      };
    },
    async apply(): Promise<void> {
      runCommand('rulemetric service install --worker-only');
    },
  },
];

const STEP_BY_ID = new Map<StepId, SetupStep>(STEPS.map((s) => [s.id, s]));

export function getStep(id: StepId): SetupStep {
  const step = STEP_BY_ID.get(id);
  if (!step) throw new Error(`Unknown setup step: ${id}`);
  return step;
}

export function isStepId(value: string): value is StepId {
  return (ALL_STEP_IDS as readonly string[]).includes(value);
}

/**
 * Keys whose values are safe to show verbatim in the env-file diff. Everything
 * NOT in this set is masked. Deny-by-default is deliberate: `~/.config/rulemetric/env`
 * can legitimately hold DATABASE_URL, SUPABASE_SERVICE_ROLE_KEY, SUPABASE_JWT_SECRET,
 * GITHUB_TOKEN, RESEND_API_KEY, etc. (see ALLOWED_ENV_VARS in service/install.ts),
 * so an allowlist-to-hide would leak any secret we forgot to enumerate. Revealing
 * only known-safe keys means a future secret added to the file is masked automatically.
 */
const SAFE_ENV_KEYS = new Set([
  'RULEMETRIC_API_URL',
  'RULEMETRIC_USER_ID',
  'RULEMETRIC_CLIENT_NAME',
  'RESEND_FROM_EMAIL',
  'SENTRY_DSN', // a DSN is not a secret
  'PG_POOL_MAX',
  'PG_IDLE_TIMEOUT',
  'PG_STATEMENT_TIMEOUT_MS',
  'NODE_ENV',
]);

/**
 * Mask secret-bearing lines in a raw env file for display. Deny-by-default:
 * every `KEY=value` line has its value masked unless KEY is in SAFE_ENV_KEYS.
 * Non-assignment lines (comments, blanks) pass through untouched.
 */
function maskEnvFile(content: string): string {
  return content
    .replace(/\n$/, '')
    .split('\n')
    .map((line) => {
      const match = line.match(/^([A-Z0-9_]+)=(.+)$/);
      if (!match) return line; // comment / blank / non-assignment
      const [, key, value] = match;
      return SAFE_ENV_KEYS.has(key) ? line : `${key}=${maskToken(value)}`;
    })
    .join('\n');
}

/**
 * Run every step's detect() and derive the overall status. `currentStepId` is
 * the first incomplete step in core order (mirrors the web reducer).
 */
export async function computeStatus(ctx: SetupContext = defaultContext()): Promise<SetupStatus> {
  const steps: StepStatus[] = [];
  for (const step of STEPS) {
    // Sequential: detect() may hit the network; order is stable + cheap.
    // eslint-disable-next-line no-await-in-loop
    const done = await step.detect(ctx);
    steps.push({ id: step.id, done });
  }
  // Monotonicity (mirrors the web reducer's `cliDetected`): the CLI binary must
  // exist to produce ANY downstream signal, so `install` greens whenever auth,
  // hooks, or worker is detected — even if `install.detect()` alone (which only
  // sees auth/worker, not hooks) came back false. Without this, a user with
  // hooks installed but no live worker sees "Install the CLI: waiting" while
  // "Install hooks: done" — a contradiction.
  const install = steps.find((s) => s.id === 'install');
  if (install && !install.done) {
    install.done = steps.some((s) => s.id !== 'install' && s.done);
  }

  // coreComplete + currentStepId are derived from CORE steps only. The proxy now
  // ships inside the `hooks` step (whose detect() greens on hooks-presence, so a
  // declined CA still completes it); any future OPTIONAL_STEP_IDS never block
  // "done" or steal the cursor. Mirrors the web reducer's optional-step handling.
  const coreIds = new Set<string>(CORE_STEP_IDS);
  const coreSteps = steps.filter((s) => coreIds.has(s.id));
  const currentStepId = coreSteps.find((s) => !s.done)?.id ?? null;
  const coreComplete = coreSteps.every((s) => s.done);
  return { steps, coreComplete, currentStepId };
}
